import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Organization } from '../services/api/organizationController';
import teamController from '../services/api/teamController';
import Logger from '../services/Logger';
import { useAuth } from '../hooks/useAuth';
import { QUERY_KEYS } from '../constants';

export interface CompanyDetails {
  organisationName?: string;
  organisationLogo?: string;
  organisationLogoUrl?: string;
  description?: unknown;
  industry?: string;
  website?: string;
  companySize?: string;
  setupStatus?: string;
  customerId?: string;
  region?: string;
  orgUuid?: string;
  isSalesDashboardAccessOrg?: boolean;
  createdAt?: string;
  updatedAt?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

interface OrganizationContextType {
  organization: Organization | null;
  setOrganization: (org: Organization | null) => void;
  clearOrganization: () => void;
  /** Full company details fetched via React Query */
  companyDetails: CompanyDetails | null;
  /** Loading state for company details */
  isLoadingCompany: boolean;
  /** Refetch company details */
  refetchCompany: () => void;
}

interface AuthUser {
  username?: string;
  email?: string;
  name?: string;
  givenName?: string;
  familyName?: string;
  sub?: string;
}

interface AuthState {
  user?: AuthUser | null;
  isLoading?: boolean;
  tokens?: { idToken?: { jwtToken?: string } };
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(
  undefined,
);

const ORGANIZATION_STORAGE_KEY = 'wisedoc_selected_organization';

export const OrganizationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const auth = useAuth() as AuthState;
  const authUser = auth.user || null;
  const authToken = auth.tokens?.idToken?.jwtToken || '';
  const isLoadingAuth = auth.isLoading || false;
  const queryClient = useQueryClient();
  const [organization, setOrganizationState] = useState<Organization | null>(
    () => {
      // Load from localStorage on mount
      try {
        const stored = localStorage.getItem(ORGANIZATION_STORAGE_KEY);
        if (stored) {
          return JSON.parse(stored);
        }
      } catch (error) {
        Logger.LogError(error);
      }
      return null;
    },
  );

  const orgUuid = organization?.orgUuid;

  useEffect(() => {
    if (authUser) {
      const displayName =
        authUser.name ||
        [authUser.givenName, authUser.familyName].filter(Boolean).join(' ') ||
        authUser.email ||
        authUser.username ||
        authUser.sub;

      Logger.SetUserLogScope(
        authUser.sub || authUser.username || authUser.email,
        displayName,
        authUser.email,
      );
      return;
    }

    if (!isLoadingAuth) {
      Logger.ClearUserLogScope();
    }
  }, [
    authUser?.email,
    authUser?.familyName,
    authUser?.givenName,
    authUser?.name,
    authUser?.sub,
    authUser?.username,
    isLoadingAuth,
  ]);

  // Fetch company details via React Query
  const {
    data: companyData,
    isLoading: isLoadingCompany,
    refetch: refetchCompany,
  } = useQuery({
    queryKey: QUERY_KEYS.COMPANY(orgUuid || ''),
    queryFn: () => teamController.getCompany(orgUuid!, authToken),
    enabled: !!authToken && !!orgUuid,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Extract company details from the response
  const companyDetails: CompanyDetails | null =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (companyData as any)?.data?.company || null;

  const setOrganization = (org: Organization | null) => {
    setOrganizationState(org);
    // Persist to localStorage
    if (org) {
      try {
        localStorage.setItem(ORGANIZATION_STORAGE_KEY, JSON.stringify(org));
      } catch (error) {
        Logger.LogError(error);
      }
    } else {
      try {
        localStorage.removeItem(ORGANIZATION_STORAGE_KEY);
        // Clear company query cache when organization is cleared
        if (orgUuid) {
          queryClient.removeQueries({ queryKey: QUERY_KEYS.COMPANY(orgUuid) });
        }
      } catch (error) {
        Logger.LogError(error);
      }
    }
  };

  const clearOrganization = () => {
    setOrganization(null);
  };

  return (
    <OrganizationContext.Provider
      value={{
        organization,
        setOrganization,
        clearOrganization,
        companyDetails,
        isLoadingCompany,
        refetchCompany,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = (): OrganizationContextType => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error(
      'useOrganization must be used within an OrganizationProvider',
    );
  }
  return context;
};
